import { t as localeText } from "@/shared/lib/translate";
import { api } from "./client";
import type { components } from "./contract";
import { unwrap } from "./errors";

type V2Asset = components["schemas"]["V2Asset"];
type V2AssetBulkDeleteReceipt = components["schemas"]["V2AssetBulkDeleteReceipt"];
type V2AssetDownload = components["schemas"]["V2AssetDownload"];
type V2Tag = components["schemas"]["V2Tag"];
type V2Collection = components["schemas"]["V2Collection"];
type V2SpaceMember = components["schemas"]["V2SpaceMember"];

export async function listSpaceMembers(spaceID: string): Promise<V2SpaceMember[]> {
  return unwrap(
    await api.GET("/v1/spaces/{id}/members", { params: { path: { id: spaceID } } }),
  ).items;
}

export async function getAsset(assetID: string): Promise<V2Asset> {
  return unwrap(
    await api.GET("/v1/assets/{id}", {
      params: { path: { id: assetID } },
    }),
  );
}

export async function updateAssetMetadata(
  asset: V2Asset,
  patch: { name?: string; visibility?: V2Asset["visibility"]; review_status?: V2Asset["review_status"]; creative_style?: string },
): Promise<V2Asset> {
  return unwrap(
    await api.PATCH("/v1/assets/{id}", {
      params: { path: { id: asset.id } },
      body: { expected_version: asset.version, ...patch },
    }),
  );
}

export async function setAssetFavorite(asset: V2Asset, favorite: boolean): Promise<void> {
  if (favorite) {
    unwrap(
      await api.POST("/v1/assets/{id}/favorite", {
        params: { path: { id: asset.id } },
      }),
    );
    return;
  }
  unwrap(
    await api.DELETE("/v1/assets/{id}/favorite", {
      params: { path: { id: asset.id } },
    }),
  );
}

export async function deleteAssets(
  assets: V2Asset[],
): Promise<V2AssetBulkDeleteReceipt> {
  const receipt: V2AssetBulkDeleteReceipt = { deleting_ids: [], failures: [] };
  for (let offset = 0; offset < assets.length; offset += 100) {
    const batch = unwrap(await api.POST("/v1/assets/bulk/delete", {
      body: { assets: assets.slice(offset, offset + 100).map((asset) => ({ id: asset.id, expected_version: asset.version })) },
    }));
    receipt.deleting_ids.push(...batch.deleting_ids);
    receipt.failures.push(...batch.failures);
  }
  return receipt;
}

export async function downloadAssets(
  assets: V2Asset[],
): Promise<V2AssetDownload[]> {
  return unwrap(
    await api.POST("/v1/assets/bulk/download", {
      body: { asset_ids: assets.map((asset) => asset.id) },
    }),
  ).items;
}

export async function listTags(): Promise<V2Tag[]> {
  return unwrap(await api.GET("/v1/tags")).items;
}

export async function createTag(name: string, color?: string): Promise<V2Tag> {
  return unwrap(await api.POST("/v1/tags", { body: { name, ...(color ? { color } : {}) } }));
}

export async function setAssetTag(
  assetID: string,
  tagID: string,
  mode: "add" | "remove",
): Promise<void> {
  return setAssetTags([assetID], [tagID], mode);
}

export async function setAssetTags(
  assetIDs: string[],
  tagIDs: string[],
  mode: "add" | "remove",
): Promise<void> {
  const assets = [...new Set(assetIDs)];
  const tags = [...new Set(tagIDs)];
  for (let assetOffset = 0; assetOffset < assets.length; assetOffset += 100) {
    for (let tagOffset = 0; tagOffset < tags.length; tagOffset += 100) {
      unwrap(await api.POST("/v1/assets/bulk/tags", {
        body: { asset_ids: assets.slice(assetOffset, assetOffset + 100), tag_ids: tags.slice(tagOffset, tagOffset + 100), mode },
      }));
    }
  }
}

export async function listCollections(): Promise<V2Collection[]> {
  const collections: V2Collection[] = [];
  let cursor: string | undefined;
  const seenCursors = new Set<string>();
  do {
    const page = unwrap(
      await api.GET("/v1/collections", {
        params: {
          query: { limit: 100, ...(cursor ? { cursor } : {}) },
        },
      }),
    );
    collections.push(...page.items);
    cursor = page.next_cursor;
    if (cursor) {
      if (seenCursors.has(cursor)) {
        throw new Error(localeText("interface.collection_pagination_returned_a_repeated_cursor_24ac48dc", { defaultValue: "Collection pagination returned a repeated cursor." }));
      }
      seenCursors.add(cursor);
    }
  } while (cursor);
  return collections;
}

export interface CollectionDraft {
  projectID?: string;
  name: string;
  description?: string;
  kind: "manual" | "smart";
  visibility: V2Collection["visibility"];
  parentID?: string;
  query?: components["schemas"]["V2SmartCollectionQuery"];
}

export async function createCollection(draft: CollectionDraft): Promise<V2Collection> {
  return unwrap(
    await api.POST("/v1/collections", {
      body: {
        name: draft.name,
        description: draft.description,
        kind: draft.kind,
        visibility: draft.visibility,
        query: draft.kind === "smart" ? (draft.query ?? {}) : {},
        ...(draft.parentID ? { parent_id: draft.parentID } : {}),
        ...(draft.projectID ? { project_id: draft.projectID } : {}),
      },
    }),
  );
}

export async function setAssetCollection(
  collectionID: string,
  assetID: string,
  mode: "add" | "remove",
): Promise<void> {
  unwrap(
    await api.POST("/v1/collections/{id}/assets", {
      params: { path: { id: collectionID } },
      body: { asset_ids: [assetID], mode },
    }),
  );
}

export async function createAssetShare(assetID: string, permission: "view" | "download") {
  return unwrap(await api.POST("/v1/assets/{id}/shares", {
    params: { path: { id: assetID } }, body: { permission },
  }));
}
